import {
  GraphQLInputType,
  GraphQLSchema,
  isEnumType,
  isInputObjectType,
  isInterfaceType,
  isObjectType,
  isScalarType,
  isUnionType,
} from 'graphql';
import { sortBy } from 'lodash';
import graphqlPlugin from 'prettier/plugins/graphql';
import { format } from 'prettier/standalone';

import { getArgumentDefinition } from 'src/app/graph/explorerPage/explorerDocs/getFieldToggle/getInsertion';
import { unwrapNestedType } from 'src/lib/graphql/graphqlHelpers';

import { CollectionEntryState } from './useEditorTabState/collectionEntryState';

/**
 * Construct a default query based on the given schema.
 */
export const getDefaultTabState = async (
  schema: GraphQLSchema,
): Promise<CollectionEntryState> => {
  const queryType = schema.getQueryType();
  const sortedFields = sortBy(
    Object.values(queryType?.getFields() ?? {}).filter((field) => {
      const type = unwrapNestedType(field.type);
      return !isUnionType(type) && !isInputObjectType(type);
    }),
    (field) => field.args.length,
  );
  // If there are no fields without args, use a single field with the least,
  // otherwise only use fields with none (max three).
  const fields = sortedFields
    .slice(0, 3)
    .filter(({ args }, index) => index === 0 || args.length === 0);
  const addedArguments: {
    argumentName: string;
    variableName: string | null;
    type: GraphQLInputType;
  }[] = [];
  const fieldsText = fields
    .flatMap((field) => {
      const type = unwrapNestedType(field.type);
      const argumentDefinition = getArgumentDefinition(field, [], true);
      if (isScalarType(type) || isEnumType(type)) {
        addedArguments.push(...argumentDefinition.addedArguments);
        return `${field.name}${argumentDefinition.definition}`;
      }
      if (isInterfaceType(type) || isObjectType(type)) {
        const nestedFields = type.getFields();
        const scalarNestedField = Object.values(nestedFields).find(
          (nestedField) => {
            if (nestedField.args.length > 0) return false;
            const nestedFieldType = unwrapNestedType(nestedField.type);
            return isScalarType(nestedFieldType) || isEnumType(nestedFieldType);
          },
        );
        if (scalarNestedField) {
          addedArguments.push(...argumentDefinition.addedArguments);
          return `${field.name}${argumentDefinition.definition} { ${scalarNestedField.name} }`;
        }
      }
      return [];
    })
    .join('\n');
  const variables = addedArguments.length
    ? `(${addedArguments
        .map((a) => `$${a.variableName}: ${a.type}`)
        .join(', ')})`
    : '';

  if (fieldsText) {
    return {
      operation: await format(
        `query ExampleQuery${variables} {
        ${fieldsText}
      }`,
        {
          parser: 'graphql',
          plugins: [graphqlPlugin],
        },
      ),
    };
  }
  return { operation: 'query Query {}' };
};
